// organize-imports-ignore — import order here is load-bearing.
/**
 * App-side wiring for the Work-home surfaces behind `@heidi/agents`.
 *
 * Every member crosses BY REFERENCE and is called where it was called before
 * the move: the landing's desktop-target picker is what enables the roster
 * query, the Library thumbnail's signed-URL read is scoped to a mounted card,
 * and the default-landing banner's preference read is gated on the rest of its
 * chain — resolving any of them here would move a query to a render that does
 * not happen today.
 *
 * IMPORTED FOR EFFECT BY `src/components/layout/scribe-layout.tsx`, not by the
 * Work route. The route is a Chat file, and an `@/utils` import there raises the
 * Chat ledger's zone count, which is a ruling rather than a migration. The
 * layout is the route's own static import and is evaluated before it renders.
 */
import {
  IS_CHAT_ATTACHMENTS_ENABLED,
  useIsAskHeidiSourcesShortcutsEnabled,
} from '@/app-shell/flags/evidence-flags';
import { copilotRunContextByProjectAtom } from '@/app-shell/copilot/copilot-run-sessions';
import { getAuthToken } from '@/app-shell/rpa/agent-client/auth-token';
import { useDesktopPermissionGate } from '@/app-shell/rpa/desktop-permission-gate';
import { buildRecorderUploadMetadata } from '@/app-shell/rpa/recorder/upload/upload-auth-context';
import { EvidenceRegulatoryFootnoteWithIfu } from '@/app-shell/settings/account/components/evidence-regulatory-footnote-with-ifu';
import { useEvidenceIfuHref } from '@/app-shell/settings/account/components/useIsEvidenceIfuVisible';
import { DEFAULT_LANDING_PAGE_SETTINGS_HASH } from '@/app-shell/settings/display-controls/components/default-landing-page/landing-page-options';
import { useQueryDefaultLandingPage } from '@/app-shell/settings/display-controls/components/default-landing-page/useDefaultLandingPageSetting';
import { useSettingsModal } from '@/app-shell/settings/hooks/useSettingsModal';
import { readyToRequestMicPermissionsAtom } from '@/atoms/audioAtoms';
import {
  hasBootLandedOnHomeAtom,
  isHomeBootPath,
  readBootPathname,
} from '@/atoms/bootLandingAtoms';
import { useOfflinePendingServerDeleteIds } from '@/atoms/offline-recording';
import { GET_SESSIONS_LIST, TabType } from '@heidi/session/contracts';
import {
  flattenSessionListPages,
  sessionWindowsAtomFamily,
  useQuerySessionsList,
  useQuerySessionWindows,
  useSessionWindow,
  useSessionWindows,
  activeSessionIdAtom,
  useMutateUpdateSessionRecordingStatus,
} from '@heidi/session/data';
import { SplitScreenSkeletonLoader as SessionSplitScreenSkeletonLoader } from '@heidi/session';

import { DEFAULT_DATE_FORMAT } from '@/app-shell/constants';
import { cancelNavMenuSwitchTracking } from '@/app-shell/layout/Navigation/sidebar/nav-menu-switch-tracking';
import { SidebarNavRowStatusIcon } from '@/app-shell/layout/Navigation/sidebar/sidebar-nav-row-status';
import { resolveChatSidebarNavRowStatus } from '@/app-shell/layout/Navigation/sidebar/sidebar-nav-row-status.resolve';
import { isShortcutsDisabledAtom } from '@/app-shell/layout/ShortcutsHelper/shortcutsAtom';
import { mobileAppHeaderMessages } from '@/app-shell/layout/header/mobile-app-header/mobile-app-header.messages';
import { DesktopBars } from '@heidi/capture';
// migration-allow(family-importers): Work home binds app mutations for the moved form save hook
import { useMutateCreatePDFTemplate } from '@/hooks/mutation/useMutateCreatePDFTemplate';
// migration-allow(family-importers): Work home binds app mutations for the moved form save hook
import { useMutateUpdateTemplate } from '@/hooks/mutation/useMutateUpdateTemplate';
import { useSaveFormTemplate } from '@heidi/templates';
import { usePresentation } from '@heidi/agents';
import {
  handleSlideDeleteChunk,
  handleSlideUpdateChunk,
} from '@/components/slide-builder/hooks/useSlideUpdateListener';
import { slideBuilderMessages } from '@/components/slide-builder/slide-builder.messages';
import {
  useSlideToolbarActions,
  useSlideUndoRedoHotkeys,
} from '@/components/slide-builder/useSlideToolbarActions';
import {
  BoldButton,
  BulletListButton,
  ItalicButton,
} from '@/components/tiptap/shared/text-menu-buttons';
import { mergeCreditAllowanceGhost } from '@/components/ui/agent-credit-allowance-banner/mergeCreditAllowanceGhost';
import { ImageFileCard } from '@heidi/agents';
import { ComposerDictateSlot } from '@/components/ui/composer-dictate-slot';
import { renderPlaceholderSlots } from '@/components/ui/composer-placeholder-slots';
import { getResolvedShortcutsForMarket } from '@/components/ui/floating-ask-heidi/shortcut-marketplace-data';
import {
  buildSearchableShortcuts,
  filterShortcuts,
} from '@/components/ui/floating-ask-heidi/shortcut-search';
import { anyDialogDrawerOpenAtom } from '@/components/ui/mobile-responsive-dialog-drawer/atoms';
import { useComposerShortcutsFlyout } from '@/components/ui/session-ask-heidi/use-in-session-shortcut-flyout';
import {
  usePersistedWorkMode,
  useSetUnsavedWorkComposerMode,
  useStartEvidenceChat,
} from '@/components/ui/session-ask-heidi/use-persisted-work-mode';
import { DraftNoteBody } from '@/components/ui/work-tool-cards/DraftNoteBody';
import { OpenTemplateEditorBlock } from '@heidi/agents';
import { TemplatePinBlock } from '@heidi/agents';
import { WorkArtefactCreatedCard } from '@heidi/agents';
import { invalidateShellCachesOnEvent } from '@/composition/hosts/agents/agent-events/invalidate-agent-event-shell-caches';
import {
  candidateMappingKey,
  candidateMappingsAtom,
  clearCandidateMappingsAtom,
  ingestFieldCandidatesAtom,
  ingestNotePushHintsAtom,
  ingestNotePushRecordedAtom,
  setSectionOverrideAtom,
} from '@/app-shell/rpa/computer-use-note-push/candidate-mappings';
import { useOpenRoutineCreateChat } from '@/composition/hosts/agents/routines/useOpenRoutineCreateChat';
import { useOpenRoutinesSurface } from '@/composition/hosts/agents/routines/useOpenRoutinesSurface';
import { detectShortcutConflict } from '@/composition/hosts/agents/routines/workflow-shortcut-conflict';
import { ChatObjectTokenHoverCard } from '@/composition/hosts/agents/chat-object-refs/chat-object-hover-card';
import {
  ChatObjectReference,
  RefMarkdownElement,
} from '@/composition/hosts/agents/chat-object-refs/chat-object-reference';
import { useCachedReferenceLabel } from '@/composition/hosts/agents/chat-object-refs/chat-object-reference-cache';
import { SessionReferenceOpenContext } from '@/composition/hosts/agents/chat-object-refs/chat-object-reference-kinds';
import { patientSexAge } from '@/composition/hosts/agents/chat-object-refs/chat-object-reference-label';
import { EvidenceAnswerPortsHost } from '@/composition/hosts/evidence/answer-ports/evidence-answer-ports-host';
import { useLetterSenderActions } from '@/composition/hosts/integrations/letters/letter-sender-v2/hooks/useLetterSenderActions';
import { useOpenPatientFullPage } from '@/composition/hosts/patients/patient-profile/useOpenPatientFullPage';
import { usePatientProfileHost } from '@/composition/hosts/patients/patient-profile/usePatientProfileHost';
import { useTemplateCreationApprovalGuard } from '@/composition/hosts/templates/template-builder/useTemplateCreationApprovalGuard';
import { useEvidenceSourceOpeners } from '@/composition/hosts/evidence/answer-ports/use-evidence-answer-ports';

import {
  focusedArtifactTab,
  toFocusedArtifactTarget,
} from '@/app-shell/session-page/v2/main/focused-artifact-target';
import {
  loadSplitScreen,
  reportSplitScreenPreloadFailure,
} from '@/app-shell/session-page/session-panel/split-screen-loader';
import { ChatMessageContext } from '@/context/ChatMessageContext';
import { useWhiteLabelSafe } from '@/context/white-label';
import { AGENT_HOME_V1_FLAG_KEY } from '@/app-shell/flags/agent-home';
import {
  useIsAgentHomeV1Enabled,
  useIsChatObjectRefsEnabled,
  useIsCmdShiftHOverlayEnabled,
  useIsDefaultLandingPageSettingEnabled,
  useIsRpaRecorderEnabled,
  useIsWorkInSessionEnabled,
} from '@/app-shell/flags/agents-flags';
import {
  useIsProjectsContextualLoaderPhrasesEnabled,
  useIsProjectsEnabled,
  useIsSessionRoutineRunsEnabled,
  useIsWorkflowsEnabled,
  useIsWorkLetterArtifactEnabled,
} from '@/app-shell/flags/heidi-work-umbrella';
import { useIsNangoIntegrationsEnabled } from '@/app-shell/flags/integrations-flags';
import {
  useIsEvidenceInlineImagesEnabled,
  useIsEvidenceSubagentResidualCapabilitiesEnabled,
  useIsWorkEvidenceCitationsEnabled,
} from '@heidi/evidence/flags';
import { useIsCountrySpecificEhrSelectionEnabled } from '@heidi/integrations/flags';
import { useIsPatientListEnabled } from '@heidi/patients/flags';
import { useIsWorkSessionArtifactV2Enabled } from '@heidi/agents/flags';
import { useDefaultLandingPageBannerWaitMs } from '@/app-shell/flags/default-landing-page-banner-wait';
import { useFormFieldEditorFlags } from '@/app-shell/flags/form-field-editor-flags';
import { IS_SLIDE_BUILDER_ENABLED } from '@/app-shell/flags/agents-flags';
import { IS_INSET_DESIGN_ENABLED } from '@/app-shell/flags/shell-flags';
import { useFeatureFlagEnabled } from '@/app-shell/flags/posthog-compat';
import { WORK_HOME_OBJECT_WORKSPACE_FLAG_KEY } from '@/app-shell/flags/work-home-object-workspace-flag';
import {
  buildCitationRegistry,
  buildImageRegistry,
  parseEvidenceAgentSearchPayload,
  toChatSource,
} from '@heidi/agents';
import {
  buildCiteMarkers,
  buildImageMarkers,
  buildReferences,
  stripCiteTags,
  stripImgTags,
} from '@heidi/agents';
import { useEvidenceAnswerComponents } from '@/features/projects/components/evidence-subagent/hooks/useEvidenceAnswerComponents';
import { useProjectEvidenceSession } from '@heidi/agents';
import {
  buildEvidenceSessionFromPayload,
  readEvidenceRenderPayload,
} from '@heidi/agents';
import { evidenceSessionsAtom, handleEvidenceChunkStreamEvent } from '@heidi/agents';
import { renderCreateTemplateToolUse } from '@heidi/agents';
import { renderUpdateWordLibraryToolUse } from '@heidi/agents';
import { writeHtmlAndMarkdownToClipboard } from '@/helpers/copyHelpers/copyHelpers-v2';
import { describeClipboardError } from '@/helpers/copyHelpers/describeClipboardError';
import { getEhrProviderMetadata } from '@/helpers/ehrProviderMetadata';
import { getDomains } from '@/helpers/getDomains';
import { getRegion } from '@/helpers/getRegion';
import { useAudioLevelsSubscription } from '@/app-shell/desktop/hooks/useAudioLevelsSubscription';
import { useIsDesktopOnlineReady } from '@/hooks/desktop/useIsDesktopOnlineReady';
import { useIsEntitlementEnabled } from '@heidi/workspace/data';
import { useMutateMarkModalAsViewed, useUserCountryCode } from '@heidi/workspace/data';
import { useMutateUpdatePatientProfile } from '@/hooks/mutation/useMutateUpdatePatientProfile';
import {
  cancelChatAttachmentUpload,
  useMutateDeleteChatAttachment,
  useMutateUploadChatAttachment,
} from '@/hooks/mutation/useMutateUploadChatAttachment';
import { useOverlayProjectCreatedListener } from '@/app-shell/rpa/overlay/useOverlayProjectCreatedListener';
import { useQueryEvidenceIntegrations } from '@/hooks/query/integrations/evidence/useQueryEvidenceIntegrations';
import { NOTE_PUSH_TARGETS_QUERY_KEY } from '@heidi/agents/contracts';
import { useQueryComputerUseNotePushTargets } from '@heidi/agents/data';
import { useQueryDocumentTemplates } from '@/hooks/query/useQueryDocumentTemplates';
import { useQueryDocumentsBySessionId } from '@/hooks/query/useQueryDocumentsBySessionId';
import { useQueryKindeProperties } from '@/hooks/query/useQueryKindeProperties';
import { useQueryPatientProfiles } from '@/hooks/query/useQueryPatientProfiles';
import { usePrefetchSessionById } from '@/hooks/query/useQuerySessionById';

import { useQuerySessionsV2 } from '@/hooks/query/useQuerySessionsV2';
import { useQueryTemplateById } from '@/hooks/query/useQueryTemplateById';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { getPreviewUrl, useChatFileUpload } from '@/hooks/useChatFileUpload';
import { formatChatUploadErrorMessage } from '@/hooks/useChatFileUpload.messages';
import { useChatPaste } from '@/hooks/useChatPaste';
import { SessionIdOverrideContext, useCurrentSessionId } from '@/hooks/useCurrentIds';
import { useEvidenceAccess } from '@/hooks/useEvidenceAccess';
import { useEvidenceChatSurfaceGate } from '@/hooks/useEvidenceChatSurfaceGate';
import { useEvidenceChipsConfig } from '@/hooks/useEvidenceChipsConfig';
import {
  useBlockErrorReporter,
  useBlockErrorTracking,
} from '@/hooks/useEvidenceAnalytics';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import { useIsClinicalRecordingActive } from '@/hooks/useIsClinicalRecordingActive';
import { useIsUKOrEU } from '@/hooks/useIsUKOrEU';
import { useIsWorkDesktopNurtureBannerEnabled } from '@/hooks/useIsWorkDesktopNurtureBannerEnabled';
import { useOpenEvidencePanel } from '@/hooks/useOpenEvidencePanel';
import { useOpenSessionPage } from '@/hooks/useOpenSessionPage';
import { useRecording } from '@/hooks/useRecording/useRecording';
import {
  useRecordingControlCommands,
  type RecordingControlResult,
} from '@/hooks/useRecording/useRecordingControlCommands';
import {
  usePendingWorkFirstTurnHandoff,
  useSessionWorkLandingResearch,
  useSessionWorkLandingViewModel,
} from '@/hooks/useSessionWorkLandingViewModel';
import { getPlatform } from '@/lib/analytics/legacy-sender-context';
import { isLogoutInFlight } from '@/lib/authInFlight';
import { cn } from '@/lib/cn';
import { FORM_FIELD_EDITOR_PORTS } from '@/lib/form-field-editor-ports';
import { isModalClosed } from '@/lib/modalClosedRules';
import { parseTimeStamp, parseTimeStampOrNull } from '@heidi/utils';
import { ReactPdfDocument, ReactPdfPage } from '@heidi/utils';
import { hotkeysList } from '@/lists/hotkeys';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  resolveWorkBillingAnalyticsProps,
  trackWorkArtifactCreated,
  trackWorkArtifactExported,
  trackWorkUpgradeActioned,
} from '@/models/analytics/features/work';
import { useTrackWorkArtifactCanvasActions } from '@/models/analytics/features/workArtifactCanvas';
import { usePosthogFlagsReady } from '@/providers/posthog-flags';
import { store, type RootState } from '@/store';
import { SYSTEM_DEFAULT_DEVICE_MARKER } from '@/tauri/constants';
import {
  cancelNativeDictation,
  shouldUseNativeAudio,
  stopNativeDictation,
} from '@/tauri/dictation';
import { readAudioLevels } from '@/tauri/events/audio-levels';
import { isTauri } from '@/tauri/utils';
import { isOnPrem } from '@/utils/buildProfile';
import { resolveExportDocumentFilename } from '@heidi/utils';
import { resolveFormTemplateUrl } from '@/utils/formTemplate';
import { isProductFeedbackAvailable } from '@/utils/heidiReach';
import { getPdfPageDimensions } from '@/utils/pdf-fields';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { createLazyChunk } from '@/utils/retry-lazy';
import { reportBoundaryError } from '@/utils/telemetry/boundary-error';
import { EvidenceLoadingAdPlacement } from '@heidi/ads';
import { useIsEvidenceAdsEnabled } from '@heidi/ads/flags';
import type { DraftApprovalBodyRenderArgs } from '@heidi/agents';
import {
  configureWorkHomePorts,
  configureWorkHostPorts,
  loadCreateWorkflowApprovalCard,
  loadPresetSetupDialog,
  loadRoutineCreateGuidedChat,
  loadRoutineDockCarousel,
  loadRoutineOverviewPane,
  loadRoutineRecentRunsRail,
  loadRoutineRunChatHeader,
  loadWorkflowShortcutListeners,
  ParamChip,
  ProtectedActionApproval,
  TemplateParts,
  useDesktopTargetSelection,
  useRecentRunsRail,
  useReferenceableLibraryDocuments,
  useRunRoutineToChat,
  useVoiceAt,
  VoiceAssistantTrigger,
  VoiceWindow,
  type WorkHomePorts,
  type WorkHostPorts,
} from '@heidi/agents';
import type {
  TrackWorkDesktopNurtureBannerEvent,
  TrackWorkUpgradeActioned,
} from '@heidi/agents/contracts';
import {
  CUA_CLOUD_MACHINE_QUERY_KEY,
  DESKTOP_TARGETS_QUERY_KEY,
  isValidDesktopInstanceId,
} from '@heidi/agents/contracts';
import {
  classifyCuaCloudProviderState,
  cuaCloudClient,
  deriveAvailableWorkflowSkillsFromSummaries,
  deriveWorkflowDetailPresentation,
  humanizeParameterName,
  isThisComputerTarget,
  parseWorkflow,
  readNativeInstanceId,
  resolveDesktopTargetTurn,
  useAllWorkflowRuns,
  useComputerUseQueuedTarget,
  useDesktopTargetRoster,
  useExecutorStopSignal,
  useNativeInstanceId,
  useResolvableWorkflow,
  useRoutineComputerChoices,
  useRoutineForProject,
  useRunAnyWorkflow,
  useSessionWorkflowRuns,
  useUserWorkflows,
  useWorkflows,
} from '@heidi/agents/data';
import {
  getOptimizedBarHeights as getBarHeights,
  useAudioAnalyzer,
} from '@heidi/capture';
import {
  clearCreditAllowanceRefusal,
  creditAllowanceRefusalAtom,
  nudgeCreditAllowanceRefusal,
  rememberCreditAllowanceRefusal,
  shouldSkipCreditAllowanceFollowUpSend,
} from '@heidi/commerce';
import { useDictationStore } from '@heidi/dictate';
import { isEditorLive } from '@heidi/editor/core';
import { getDocumentType } from '@heidi/evidence/contracts';
import {
  evidenceDisclaimerModalOpenAtom,
  useDocumentDownloadUrl,
} from '@heidi/evidence/data';
import {
  isGetMobileAppModalOpenAtom,
  personalizationSharedMessages,
} from '@heidi/growth';
import { NangoConnectButton } from '@heidi/integrations/connected-apps';
import { useLetter } from '@heidi/integrations/letters';
import {
  lettersQueryOptions,
  pendingLettersAtomFamily,
  useQueryLetters,
} from '@heidi/integrations/letters/data';
import { useQueryPatientProfileById } from '@heidi/patients/data';
import { track as trackViaPipeline } from '@heidi/analytics';

import { OverlayShimmerText } from '@heidi/snowflake-ui/overlay-shimmer-text';
import { SearchGroupTransition } from '@heidi/snowflake-ui/search-group-transition';
import { useFormDraftTitleState, useHandleEditTemplate } from '@heidi/templates';
import { useMediaQuery } from '@heidi/utils';
import { useWhenProviderReady } from '@openfeature/react-sdk';
import { getDefaultStore, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import platform from 'platform';
import { createElement, lazy, useCallback, useMemo, type ComponentProps } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useSelector } from 'react-redux';
import {
  loadCloudComputerExpandButton,
  loadCloudComputerInlineLiveView,
  loadCloudComputerLightbox,
  loadCloudComputerLiveView,
  loadDesktopControlApprovalWarning,
} from './work-home-ports-lazy-loaders';

type ChatToolBlocks = Pick<
  WorkHostPorts['bridge'],
  'CreateTemplateBlock' | 'UpdateWordLibraryBlock'
>;

/** The two Chat answer blocks. Read at render, not at module eval. */
const chatToolBlocks = (): ChatToolBlocks => {
  // Each block is its own module, so the Chat components barrel stays on the lazy loader.
  /* oxlint-disable typescript/no-require-imports */
  /* eslint-disable @typescript-eslint/no-require-imports */
  const createTemplate = require('@/components/chat/components/CreateTemplateBlock') as {
    CreateTemplateBlock: ChatToolBlocks['CreateTemplateBlock'];
  };
  const updateWordLibrary =
    require('@/components/chat/components/UpdateWordLibraryBlock') as {
      UpdateWordLibraryBlock: ChatToolBlocks['UpdateWordLibraryBlock'];
    };
  /* oxlint-enable typescript/no-require-imports */
  /* eslint-enable @typescript-eslint/no-require-imports */
  return {
    CreateTemplateBlock: createTemplate.CreateTemplateBlock,
    UpdateWordLibraryBlock: updateWordLibrary.UpdateWordLibraryBlock,
  };
};

const OpenTemplateEditorBlockHost = (
  props: ComponentProps<typeof OpenTemplateEditorBlock>
) => {
  useBlockErrorTracking('open_template_editor', props.status, props.error);
  return createElement(OpenTemplateEditorBlock, props);
};

const TemplatePinBlockHost = (props: ComponentProps<typeof TemplatePinBlock>) => {
  useBlockErrorTracking(
    props.variant === 'pin' ? 'pin_template' : 'unpin_template',
    props.status,
    props.error
  );
  return createElement(TemplatePinBlock, props);
};

/** The landing picker, which is also what enables the desktop-target roster query. */
const useLandingDesktopTarget = () => useDesktopTargetSelection({ surface: 'landing' });

/** The boot-landing flag as a value + setter pair: the atom itself stays app-owned. */
const useHasBootLandedOnHome = (): [boolean, (next: boolean) => void] =>
  useAtom(hasBootLandedOnHomeAtom);

/** The mark-as-viewed write, unwrapped from the mutation object. */
const useMutateMarkModalAsViewedPort = () => useMutateMarkModalAsViewed().mutate;

/**
 * The two PostHog reads, each bound to its own key so the package never sees
 * one. `undefined` is a real answer — flags have not loaded — and is passed
 * through, because that is what the mount decision holds on.
 */
const useAgentHomeNavFlag = () => useFeatureFlagEnabled(AGENT_HOME_V1_FLAG_KEY);
const useObjectWorkspaceFlag = () =>
  useFeatureFlagEnabled(WORK_HOME_OBJECT_WORKSPACE_FLAG_KEY);

const buildWorkHomePorts = (): WorkHomePorts => ({
  useOpenRoutineCreateChat,
  useLandingDesktopTarget,
  useDocumentDownloadUrl,
  pdf: { Document: ReactPdfDocument, Page: ReactPdfPage },
  getDocumentType,
  useIsDefaultLandingPageSettingEnabled,
  useDefaultLandingPageBannerWaitMs,
  useIsClinicalRecordingActive,
  useHasBootLandedOnHome,
  readBootPathname,
  isHomeBootPath,
  useQueryDefaultLandingPage,
  useMutateMarkModalAsViewed: useMutateMarkModalAsViewedPort,
  useSettingsModal,
  // Settings → Display controls, scrolled to and highlighting the Default page row.
  settingsHref: `/settings/display-controls${DEFAULT_LANDING_PAGE_SETTINGS_HASH}`,
  isModalClosed,
  parseTimeStampOrNull,
  useAgentHomeNavFlag,
  useObjectWorkspaceFlag,
  usePosthogFlagsReady,
  useIsProjectsEnabled,
});

const isEscAlreadyClaimed = (event: KeyboardEvent) => event.defaultPrevented;

/** The page-wide Esc stop, registered in the app's hotkey list. */
const useStopGeneratingHotkey: WorkHostPorts['shell']['useStopGeneratingHotkey'] = (
  onStop,
  enabled
) => {
  useHotkeys(
    hotkeysList.stopGenerating.hotkeys,
    () => onStop(),
    {
      scopes: hotkeysList.stopGenerating.scopes,
      preventDefault: true,
      ignoreEventWhen: isEscAlreadyClaimed,
      enabled,
    },
    [onStop]
  );
};

const useAreShortcutsDisabled = () => useAtomValue(isShortcutsDisabledAtom);

const useNavigate = (): ReturnType<WorkHostPorts['shell']['useNavigate']> => {
  const router = useRouter();
  return useCallback(
    async (href) => {
      await router.push(href);
    },
    [router]
  );
};

const useRouteRead = () => {
  const router = useRouter();
  return {
    isReady: router.isReady,
    pathname: router.pathname,
    query: router.query,
    asPath: router.asPath,
  };
};

const useReplaceRouteQuery = () => {
  const router = useRouter();
  return useCallback(
    (query: Record<string, string | string[]>) => {
      void router.replace({ pathname: router.pathname, query }, undefined, {
        shallow: true,
      });
    },
    [router]
  );
};

const useTimerSeconds = () => useSelector((s: RootState) => s.timerModel.seconds);

/** Auth-token poke. The body is the store subscription the attach loop had inline. */
export const subscribeAuthTokenChange = (cb: () => void): (() => void) =>
  store.subscribe(cb);

/**
 * Device bootstrap the overlay ran before a desktop start/resume. No-op off
 * desktop; the marker fills a slot the native recorder has not chosen yet.
 */
export const ensureDesktopRecordingDevices = async (): Promise<void> => {
  if (!isTauri()) return;
  let recordState = store.getState().scribeRecordModel;
  if (!recordState.tauriInputDeviceSelected || !recordState.tauriOutputDeviceSelected) {
    await store.dispatch.scribeRecordModel.getTauriDevices({
      updateSelectedDevices: true,
    });
    recordState = store.getState().scribeRecordModel;
  }
  const systemDefaultDevice = {
    name: SYSTEM_DEFAULT_DEVICE_MARKER,
    is_default: true,
    display_name: null,
    icon_data_url: null,
    is_communications_default: false,
  };
  if (!recordState.tauriInputDeviceSelected) {
    store.dispatch.scribeRecordModel.setTauriInputDevice(systemDefaultDevice);
  }
  if (!recordState.tauriOutputDeviceSelected) {
    store.dispatch.scribeRecordModel.setTauriOutputDevice(systemDefaultDevice);
  }
};

const useSetIngestFieldCandidates = () => useSetAtom(ingestFieldCandidatesAtom);
const useSetIngestNotePushHints = () => useSetAtom(ingestNotePushHintsAtom);
const useSetIngestNotePushRecorded = () => useSetAtom(ingestNotePushRecordedAtom);
const useSetClearCandidateMappings = () => useSetAtom(clearCandidateMappingsAtom);

const useCopilotRunContextByProject = () => useAtomValue(copilotRunContextByProjectAtom);

const useCreditAllowanceRefusalValue = () => useAtomValue(creditAllowanceRefusalAtom);

const useSetGetMobileAppModalOpen = () => useSetAtom(isGetMobileAppModalOpenAtom);

const readEvidenceSession: WorkHostPorts['bridge']['readEvidenceSession'] = (stepId) =>
  getDefaultStore().get(evidenceSessionsAtom).get(stepId);

const subscribeEvidenceSessions: WorkHostPorts['bridge']['subscribeEvidenceSessions'] = (
  onChange
) => getDefaultStore().sub(evidenceSessionsAtom, onChange);

const usePendingLetters = (sessionId: string) =>
  useAtomValue(pendingLettersAtomFamily(sessionId));

const useActiveSessionId = () => useAtomValue(activeSessionIdAtom);
const useSetActiveSessionId = () => useSetAtom(activeSessionIdAtom);

// The overlay's pause and stop: the command boundary accepts each press, picks
// the recorder and tracks it, so the package sees none of that.
const useOverlayRecordingCommands: WorkHostPorts['session']['useRecordingCommands'] = (
  sessionId
) => {
  const recorder = useRecording();
  const { startRecording } = recorder;
  const commands = useRecordingControlCommands({
    sessionId: sessionId ?? undefined,
    recorder,
  });
  const setActiveSessionId = useSetAtom(activeSessionIdAtom);
  return useMemo(() => {
    const outcome = async ({ accepted, settled }: RecordingControlResult) => {
      await settled;
      return { accepted };
    };
    return {
      startRecording: () => startRecording(),
      pause: () =>
        outcome(
          commands.pause({
            controlSurface: 'projects_overlay',
            beforeRecorder: () => setActiveSessionId(null),
          })
        ),
      stop: ({ fromPaused }) =>
        outcome(
          commands.stop({
            controlSurface: 'projects_overlay',
            fromPaused,
            beforeRecorder: () => setActiveSessionId(null),
          })
        ),
    };
  }, [commands, setActiveSessionId, startRecording]);
};

const useIsFlagProviderReady = () => useWhenProviderReady({ suspendUntilReady: false });
const useCandidateMappings = () => useAtomValue(candidateMappingsAtom);
const useSetSectionOverride = () => useSetAtom(setSectionOverrideAtom);

const lazyNamed = (
  load: () => Promise<Record<string, unknown>>,
  exportName: string
): WorkHostPorts['session']['EmbeddingsPanel'] =>
  lazy(() =>
    load().then((mod) => ({
      default: mod[exportName] as WorkHostPorts['session']['EmbeddingsPanel'],
    }))
  );

const RecordingWorkflowSummary = lazyNamed(
  () => import('@/composition/hosts/agents/routines/RecordingWorkflowSummary'),
  'RecordingWorkflowSummary'
) as unknown as WorkHostPorts['routines']['RecordingWorkflowSummary'];

const EmbeddingsPanel = lazyNamed(
  () => import('@/composition/hosts/coding/session-panes/work-embeddings-panel'),
  'WorkEmbeddingsPanel'
);
const RegulatoryGuidelineBlockModal = lazyNamed(
  () => import('@heidi/templates'),
  'RegulatoryGuidelineBlockModal'
);
const RegulatoryGuidelineWarningModal = lazyNamed(
  () =>
    import('@/components/ui/template-builder-edit-dialog-button/regulatory-guideline-warning-modal'),
  'RegulatoryGuidelineWarningModal'
);
const PatientDocumentsSection = lazyNamed(
  () => import('@heidi/patients'),
  'PatientDocumentsSection'
);
const DrawerSections = lazyNamed(() => import('@heidi/patients'), 'DrawerSections');
const SessionHistorySection = lazyNamed(
  () => import('@heidi/patients'),
  'SessionHistorySection'
);
const SlideEditorPanel = lazyNamed(
  () => import('@/components/slide-builder/SlideEditorPanel'),
  'SlideEditorPanel'
);
const SlideUndoRedoButtons = lazyNamed(
  () => import('@/components/slide-builder/SlideToolbar'),
  'SlideUndoRedoButtons'
);
const SessionArtifactModalHosts = lazyNamed(
  () => import('@/app-shell/session-page/v2/main/SessionArtifactModalHosts'),
  'SessionArtifactModalHosts'
);
const LinkedSessionTabsHost = lazyNamed(
  () =>
    Promise.all([
      import('@/app-shell/session-page/session-panel/LinkedSessionTabsHost'),
      import('@/composition/hosts/session/session-page/session-page-host'),
      import('@/composition/hosts/coding/session-panes/coding-session-pane-host'),
      import('@/composition/hosts/agents/session-panel/session-panel-agents-host'),
    ]).then(([member, host, codingHost, panelHost]) => ({
      LinkedSessionTabsHost: host.withSessionPageHost(
        codingHost.withCodingSessionPaneHost(
          panelHost.withSessionPanelAgentsHost(member.LinkedSessionTabsHost)
        )
      ),
    })),
  'LinkedSessionTabsHost'
);
const SplitScreenSkeletonLoader = lazyNamed(
  () =>
    Promise.all([
      import('@/composition/hosts/session/session-page/session-page-host'),
    ]).then(([host]) => ({
      SplitScreenSkeletonLoader: host.withSessionPageHost(
        SessionSplitScreenSkeletonLoader
      ),
    })),
  'SplitScreenSkeletonLoader'
);
const ClinicalTasksPanelHostProvider = lazyNamed(
  () =>
    import('@/composition/hosts/agents/session-panel/clinical-tasks-panel-host-provider'),
  'ClinicalTasksPanelHostProvider'
);
const TemplateMarkdownPreview = lazyNamed(
  () => import('@/components/template-markdown-preview'),
  'TemplateMarkdownPreview'
);
const ChunkErrorMessage = lazyNamed(
  () => import('@/app-shell/ui/chunk-error-message'),
  'ChunkErrorMessage'
);
const RoutineOverviewPane = lazy(
  loadRoutineOverviewPane
) as unknown as WorkHostPorts['routines']['RoutineOverviewPane'];
const RoutineCreateGuidedChat = lazy(
  loadRoutineCreateGuidedChat
) as unknown as WorkHostPorts['routines']['RoutineCreateGuidedChat'];

// These three render only inside the Work chat. Loading them here would put
// their modules on /overlay and /cua-status, which evaluate this wiring.
const ExperienceFeedbackNudge = lazyNamed(
  () => import('@/components/experience-feedback/ExperienceFeedbackNudge'),
  'ExperienceFeedbackNudge'
) as unknown as WorkHostPorts['shell']['ExperienceFeedbackNudge'];
const InSessionSourcesButton = lazyNamed(
  () => import('@/components/ui/floating-ask-heidi/floating-ask-heidi-input'),
  'InSessionSourcesButton'
) as unknown as WorkHostPorts['evidence']['InSessionSourcesButton'];
const RoutineRunChatHeader = lazy(
  loadRoutineRunChatHeader
) as unknown as WorkHostPorts['routines']['RoutineRunChatHeader'];

// A static import of these four is on /overlay and /cua-status, which evaluate
// this wiring. Bind them the same way as RoutineRunChatHeader.
const ChatObjectList = lazyNamed(
  () => import('@/composition/hosts/agents/chat-object-refs/chat-object-list'),
  'ChatObjectList'
) as unknown as WorkHostPorts['evidence']['ChatObjectList'];
const CloudComputerInlineLiveView = lazy(loadCloudComputerInlineLiveView);
const DesktopControlApprovalWarning = lazy(loadDesktopControlApprovalWarning);

const renderProtectedActionDraftBody = ({
  body,
  isStreaming,
  tintAgainst,
  removal,
}: DraftApprovalBodyRenderArgs) =>
  createElement(appPorts().workHost.session.DraftNoteBody, {
    markdown: body,
    isStreaming,
    tintAgainst,
    removal,
  });
const protectedActionPatientContext = {
  useAuth: useIsAuthenticated,
  useProfile: useQueryPatientProfileById,
};

const ProtectedActionApprovalHost: WorkHostPorts['desktop']['ProtectedActionApproval'] =
  ({ approval }) =>
    createElement(ProtectedActionApproval, {
      approval,
      patientContext: protectedActionPatientContext,
      renderDraftBody: renderProtectedActionDraftBody,
    });
const CreateWorkflowApprovalCard = lazy(
  loadCreateWorkflowApprovalCard
) as unknown as WorkHostPorts['routines']['CreateWorkflowApprovalCard'];

const TeamsSelection: WorkHostPorts['routines']['TeamsSelection'] = lazy(() =>
  import('@/composition/hosts/templates/teams-selection/teams-selection-host').then(
    (m) => ({ default: m.TeamsSelectionHost })
  )
);

const EvidenceCircleLoader = lazyNamed(
  () => import('@/components/ui/evidence-circle-loader'),
  'EvidenceCircleLoader'
) as unknown as WorkHostPorts['routines']['EvidenceCircleLoader'];

const LinkedContextBadge = lazyNamed(
  () => import('@/components/ui/linked-context-badge'),
  'LinkedContextBadge'
);

const loadV2FocusedSessionArtifactPanel = () =>
  Promise.all([
    import('@/app-shell/session-page/v2/main/v2-focused-session-artifact-panel'),
    import('@/composition/hosts/session/session-page/session-page-host'),
    import('@/composition/hosts/coding/session-panes/coding-session-pane-host'),
    import('@/composition/hosts/agents/session-panel/session-panel-agents-host'),
  ]).then(([mod, host, codingHost, panelHost]) => ({
    default: host.withSessionPageHost(
      codingHost.withCodingSessionPaneHost(
        panelHost.withSessionPanelAgentsHost(mod.V2FocusedSessionArtifactPanel)
      )
    ) as unknown as WorkHostPorts['session']['EmbeddingsPanel'],
  }));
const loadSessionPreviewArtifact = () =>
  Promise.all([
    import('@/app-shell/session-page/v2/main/SessionPreviewArtifact'),
    import('@/composition/hosts/session/session-page/session-page-host'),
    import('@/composition/hosts/coding/session-panes/coding-session-pane-host'),
    import('@/composition/hosts/agents/session-panel/session-panel-agents-host'),
  ]).then(([mod, host, codingHost, panelHost]) => ({
    default: host.withSessionPageHost(
      codingHost.withCodingSessionPaneHost(
        panelHost.withSessionPanelAgentsHost(mod.SessionPreviewArtifact)
      )
    ) as unknown as WorkHostPorts['session']['EmbeddingsPanel'],
  }));
const loadShareTemplateModal = () =>
  import('@/components/modal/share-template-modal').then((mod) => ({
    default:
      mod.ShareTemplateModal as unknown as WorkHostPorts['session']['EmbeddingsPanel'],
  }));
const loadLetterTabPane = () =>
  Promise.all([
    import('@/composition/hosts/integrations/letters/letter-sender-v2/components/letter-tab-pane'),
    import('@/composition/hosts/session/session-page/session-page-host'),
  ]).then(([mod, host]) => ({
    default: host.withSessionPageHost(
      mod.LetterTabPane
    ) as unknown as WorkHostPorts['session']['EmbeddingsPanel'],
  }));
// projects components — bound by reference, called where the moved modules called them.
// The workflows and computer-use UI below loads on first use, never in a route's first load.
const CloudComputerExpandButton = lazy(loadCloudComputerExpandButton);
const CloudComputerLightbox = lazy(loadCloudComputerLightbox);
const RoutineRecentRunsRail = lazy(loadRoutineRecentRunsRail);
const CloudComputerLiveView = lazy(loadCloudComputerLiveView);
const RoutineDockCarousel = lazy(loadRoutineDockCarousel);
const WorkflowsSplitView = lazyNamed(
  () => import('@/composition/hosts/agents/routines/workflows-split-view-host'),
  'WorkflowsSplitViewHost'
);
// projects PR4a — Chat-bound components, one lazy boundary each, never per mount.
export const loadEvidenceTimelineBody = () =>
  Promise.all([
    import('@/features/projects/components/EvidenceTimelineBody'),
    import('@/components/chat/components/components'),
  ]).then(([body, chat]) => ({
    default: (
      props: Omit<ComponentProps<typeof body.EvidenceTimelineBody>, 'responsePorts'>
    ) =>
      createElement(body.EvidenceTimelineBody, {
        ...props,
        responsePorts: chat.chatResponsePorts,
      }),
  }));

const EvidenceTimelineBody = lazy(loadEvidenceTimelineBody);
const WorkSourcesPopover = lazy(() =>
  import('@/features/projects/components/LinkContextButton.parts').then((mod) => ({
    default: mod.WorkSourcesPopover,
  }))
);
const ComposerShortcutMarketplace = lazy(() =>
  import('@/components/ui/floating-ask-heidi/composer-shortcut-marketplace').then(
    (mod) => ({
      default: mod.ComposerShortcutMarketplace,
    })
  )
);

const useReadyToRequestMicPermissions = () =>
  useAtomValue(readyToRequestMicPermissionsAtom);
const useSetEvidenceDisclaimerModalOpen = () =>
  useSetAtom(evidenceDisclaimerModalOpenAtom);
const useAnyDialogDrawerOpen = () => useAtomValue(anyDialogDrawerOpenAtom);

const isMacOS = (): boolean => {
  const family = platform.os?.family?.toLowerCase();
  return family?.includes('mac') === true || platform.os?.family === 'OS X';
};

const trackNurtureEvent: TrackWorkDesktopNurtureBannerEvent = (eventName, props) => {
  trackViaPipeline({
    event_name: eventName,
    props: {
      surface: 'work_home',
      region: getRegion(),
      ...props,
    },
    options: { amplitude: false, posthog: false },
  });
};

const trackUpgradeActioned: TrackWorkUpgradeActioned = (props) =>
  trackWorkUpgradeActioned({ ...props, ...resolveWorkBillingAnalyticsProps() });

const useRunRoutineToChatPort = (): ((workflowId: string) => void) => {
  const run = useRunRoutineToChat();
  return (workflowId: string) => {
    run(workflowId);
  };
};

// The page is read above the override, so a session page keeps its own session:
// the same precedence `useRecordingSessionId` applies.
const RecorderSessionScope: WorkHostPorts['session']['RecorderSessionScope'] = ({
  sessionId,
  children,
}) => {
  const pageSessionId = useCurrentSessionId();
  return createElement(
    SessionIdOverrideContext.Provider,
    { value: pageSessionId ? null : sessionId },
    children
  );
};

const useAskHeidiTemplateCreationApprovalGuard: WorkHostPorts['desktop']['useTemplateCreationApprovalGuard'] =
  () => useTemplateCreationApprovalGuard('ask_heidi_approval');

const useHostedSaveFormTemplate: WorkHostPorts['session']['useSaveFormTemplate'] = () => {
  const { mutateAsync: createPDFTemplate } = useMutateCreatePDFTemplate();
  const { mutateAsync: updateTemplate } = useMutateUpdateTemplate();
  const ports = useSaveFormTemplate({ createPDFTemplate, updateTemplate });
  return {
    saveFormTemplate: (input: unknown, saveAnyway: boolean) =>
      ports.saveFormTemplate(
        input as Parameters<typeof ports.saveFormTemplate>[0],
        saveAnyway
      ),
  };
};

const buildWorkHostBundles = (workHome: WorkHomePorts): WorkHostPorts => ({
  ...workHome,
  desktop: {
    useUserWorkflows: useUserWorkflows as WorkHostPorts['desktop']['useUserWorkflows'],
    deriveAvailableWorkflowSkillsFromSummaries:
      deriveAvailableWorkflowSkillsFromSummaries as unknown as WorkHostPorts['desktop']['deriveAvailableWorkflowSkillsFromSummaries'],
    useSetIngestFieldCandidates:
      useSetIngestFieldCandidates as WorkHostPorts['desktop']['useSetIngestFieldCandidates'],
    useSetIngestNotePushHints:
      useSetIngestNotePushHints as WorkHostPorts['desktop']['useSetIngestNotePushHints'],
    useSetIngestNotePushRecorded:
      useSetIngestNotePushRecorded as WorkHostPorts['desktop']['useSetIngestNotePushRecorded'],
    useSetClearCandidateMappings,
    notePushTargetsQueryKey: NOTE_PUSH_TARGETS_QUERY_KEY,
    useIsExecutorStopSignalled: useExecutorStopSignal,
    getAuthToken,
    isThisComputerTarget,
    useNativeInstanceId,
    useDesktopTargetRoster,
    getDomains,
    buildRecorderUploadMetadata,
    useCopilotRunContextByProject,
    useDesktopPermissionGate,
    deriveWorkflowDetailPresentation,
    parseWorkflow,
    DesktopBars,
    SYSTEM_DEFAULT_DEVICE_MARKER,
    // projects artifacts
    candidateMappingKey,
    useCandidateMappings,
    useSetSectionOverride:
      useSetSectionOverride as WorkHostPorts['desktop']['useSetSectionOverride'],
    useQueryComputerUseNotePushTargets:
      useQueryComputerUseNotePushTargets as WorkHostPorts['desktop']['useQueryComputerUseNotePushTargets'],
    // projects components
    ParamChip,
    TemplateParts,
    humanizeParameterName,
    CloudComputerExpandButton: CloudComputerExpandButton,
    CloudComputerLightbox,
    CloudComputerLiveView: CloudComputerLiveView,
    useOverlayProjectCreatedListener,
    invalidateShellCachesOnEvent,
    useIsWorkDesktopNurtureBannerEnabled,
    OverlayShimmerText,
    isTauri,
    // projects PR4a
    useReadyToRequestMicPermissions,
    readTauriAudioLevels: readAudioLevels,
    getBarHeights,
    useAudioAnalyzer,
    isValidDesktopInstanceId,
    readNativeInstanceId,
    resolveDesktopTargetTurn,
    useDesktopTargetSelection,
    useAudioLevelsSubscription,
    cancelNativeDictation,
    shouldUseNativeAudio,
    stopNativeDictation,
    // projects PR4b
    classifyCuaCloudProviderState,
    CUA_CLOUD_MACHINE_QUERY_KEY,
    cuaCloudClient: cuaCloudClient,
    useTemplateCreationApprovalGuard: useAskHeidiTemplateCreationApprovalGuard,
    CloudComputerInlineLiveView,
    DesktopControlApprovalWarning: DesktopControlApprovalWarning,
    DESKTOP_TARGETS_QUERY_KEY,
    useComputerUseQueuedTarget: useComputerUseQueuedTarget,
    ProtectedActionApproval: ProtectedActionApprovalHost,
    useIsDesktopOnlineReady,
    useRoutineComputerChoices,
  },
  session: {
    useOfflinePendingServerDeleteIds,
    flattenSessionListPages,
    useQuerySessionsList,
    useQueryPatientProfiles,
    useQuerySessionsV2,
    usePrefetchSessionById,
    useSessionWindow: useSessionWindow as WorkHostPorts['session']['useSessionWindow'],
    useSessionWindows,
    useQuerySessionWindows,
    useActiveSessionId,
    useSetActiveSessionId,
    useMutateUpdateSessionRecordingStatus,
    useRecordingCommands: useOverlayRecordingCommands,
    useTimerSeconds,
    ensureDesktopRecordingDevices,
    RecorderSessionScope,
    // projects artifacts
    SessionIdOverrideContext,
    EmbeddingsPanel,
    useSaveFormTemplate: useHostedSaveFormTemplate,
    RegulatoryGuidelineBlockModal,
    RegulatoryGuidelineWarningModal,
    useFormFieldEditorFlags: useFormFieldEditorFlags,
    useQueryTemplateById:
      useQueryTemplateById as WorkHostPorts['session']['useQueryTemplateById'],
    useFormDraftTitleState:
      useFormDraftTitleState as WorkHostPorts['session']['useFormDraftTitleState'],
    FORM_FIELD_EDITOR_PORTS,
    resolveFormTemplateUrl:
      resolveFormTemplateUrl as unknown as WorkHostPorts['session']['resolveFormTemplateUrl'],
    getPdfPageDimensions:
      getPdfPageDimensions as WorkHostPorts['session']['getPdfPageDimensions'],
    PatientDocumentsSection,
    DrawerSections,
    SessionHistorySection,
    useOpenPatientFullPage,
    useMutateUpdatePatientProfile:
      useMutateUpdatePatientProfile as unknown as WorkHostPorts['session']['useMutateUpdatePatientProfile'],
    usePresentation: usePresentation,
    slideBuilderMessages: slideBuilderMessages,
    SlideEditorPanel,
    SlideUndoRedoButtons,
    useSlideToolbarActions: useSlideToolbarActions,
    useSlideUndoRedoHotkeys: useSlideUndoRedoHotkeys,
    sessionWindowsAtomFamily,
    SessionArtifactModalHosts,
    LinkedSessionTabsHost,
    loadSplitScreen,
    reportSplitScreenPreloadFailure,
    focusedArtifactTab:
      focusedArtifactTab as WorkHostPorts['session']['focusedArtifactTab'],
    toFocusedArtifactTarget:
      toFocusedArtifactTarget as WorkHostPorts['session']['toFocusedArtifactTarget'],
    SplitScreenSkeletonLoader,
    useOpenSessionPage:
      useOpenSessionPage as WorkHostPorts['session']['useOpenSessionPage'],
    loadV2FocusedSessionArtifactPanel,
    loadSessionPreviewArtifact,
    ClinicalTasksPanelHostProvider,
    TemplateMarkdownPreview,
    useHandleEditTemplate: () => useHandleEditTemplate(),
    writeHtmlAndMarkdownToClipboard:
      writeHtmlAndMarkdownToClipboard as unknown as WorkHostPorts['session']['writeHtmlAndMarkdownToClipboard'],
    describeClipboardError,
    useQueryDocumentTemplates: useQueryDocumentTemplates,
    loadShareTemplateModal,
    GET_SESSIONS_LIST,
    useQueryDocumentsBySessionId: useQueryDocumentsBySessionId,
    usePatientProfileHost: usePatientProfileHost,
    // projects PR4a
    handleSlideDeleteChunk: handleSlideDeleteChunk,
    handleSlideUpdateChunk: handleSlideUpdateChunk,
    isEditorLive: isEditorLive as WorkHostPorts['session']['isEditorLive'],
    TabType,
    useDictationStore: useDictationStore,
    // projects PR4b
    DraftNoteBody: DraftNoteBody,
    useTemplateCreationApprovalGuard: useTemplateCreationApprovalGuard,
    usePendingWorkFirstTurnHandoff:
      usePendingWorkFirstTurnHandoff as unknown as WorkHostPorts['session']['usePendingWorkFirstTurnHandoff'],
    useSessionWorkLandingResearch: useSessionWorkLandingResearch,
    useSessionWorkLandingViewModel: useSessionWorkLandingViewModel,
  },
  evidence: {
    getResolvedShortcutsForMarket,
    buildSearchableShortcuts,
    useEvidenceChipsConfig,
    patientSexAge,
    cancelChatAttachmentUpload,
    useMutateDeleteChatAttachment,
    useMutateUploadChatAttachment,
    useChatFileUpload:
      useChatFileUpload as WorkHostPorts['evidence']['useChatFileUpload'],
    formatChatUploadErrorMessage,
    useEvidenceIfuHref,
    useEvidenceChatSurfaceGate,
    // projects artifacts
    LinkedContextBadge,
    // projects components
    SearchGroupTransition:
      SearchGroupTransition as unknown as WorkHostPorts['evidence']['SearchGroupTransition'],
    ChatObjectReference:
      ChatObjectReference as unknown as WorkHostPorts['evidence']['ChatObjectReference'],
    RefMarkdownElement: RefMarkdownElement,
    resolveExportDocumentFilename,
    // projects PR4a
    useSetEvidenceDisclaimerModalOpen: useSetEvidenceDisclaimerModalOpen,
    ImageFileCard: ImageFileCard as unknown as WorkHostPorts['evidence']['ImageFileCard'],
    ComposerShortcutMarketplace:
      ComposerShortcutMarketplace as unknown as WorkHostPorts['evidence']['ComposerShortcutMarketplace'],
    filterShortcuts: filterShortcuts,
    useComposerShortcutsFlyout: useComposerShortcutsFlyout,
    ChatObjectTokenHoverCard:
      ChatObjectTokenHoverCard as unknown as WorkHostPorts['evidence']['ChatObjectTokenHoverCard'],
    useCachedReferenceLabel:
      useCachedReferenceLabel as WorkHostPorts['evidence']['useCachedReferenceLabel'],
    EvidenceAnswerPortsHost:
      EvidenceAnswerPortsHost as unknown as WorkHostPorts['evidence']['EvidenceAnswerPortsHost'],
    ChatMessageContext:
      ChatMessageContext as unknown as WorkHostPorts['evidence']['ChatMessageContext'],
    EvidenceRegulatoryFootnoteWithIfu:
      EvidenceRegulatoryFootnoteWithIfu as unknown as WorkHostPorts['evidence']['EvidenceRegulatoryFootnoteWithIfu'],
    useQueryEvidenceIntegrations: useQueryEvidenceIntegrations,
    getPreviewUrl: getPreviewUrl,
    useChatPaste: useChatPaste,
    useEvidenceAccess: useEvidenceAccess,
    // projects PR4b
    useEvidenceSourceOpeners:
      useEvidenceSourceOpeners as WorkHostPorts['evidence']['useEvidenceSourceOpeners'],
    InSessionSourcesButton: InSessionSourcesButton,
    usePersistedWorkMode,
    useSetUnsavedWorkComposerMode,
    useStartEvidenceChat,
    SessionReferenceOpenContext,
    ChatObjectList: ChatObjectList,
  },
  integrations: {
    usePendingLetters,
    // projects artifacts
    useLetter: useLetter as WorkHostPorts['integrations']['useLetter'],
    useQueryLetters: useQueryLetters,
    useLetterSenderActions: useLetterSenderActions,
    loadLetterTabPane,
    // projects components
    NangoConnectButton,
    getEhrProviderMetadata: getEhrProviderMetadata,
    // projects PR4a
    lettersQueryOptions:
      lettersQueryOptions as unknown as WorkHostPorts['integrations']['lettersQueryOptions'],
  },
  routines: {
    useWorkflows,
    useReferenceableLibraryDocuments,
    // projects artifacts
    useVoiceAt: useVoiceAt as WorkHostPorts['routines']['useVoiceAt'],
    VoiceWindow: VoiceWindow as unknown as WorkHostPorts['routines']['VoiceWindow'],
    RoutineOverviewPane,
    useResolvableWorkflow,
    // projects components
    RoutineRecentRunsRail:
      RoutineRecentRunsRail as unknown as WorkHostPorts['routines']['RoutineRecentRunsRail'],
    useRoutineForProject: useRoutineForProject,
    useRecentRunsRail,
    useAllWorkflowRuns: useAllWorkflowRuns,
    useSessionWorkflowRuns: useSessionWorkflowRuns,
    loadPresetSetupDialog,
    RoutineDockCarousel:
      RoutineDockCarousel as unknown as WorkHostPorts['routines']['RoutineDockCarousel'],
    WorkflowsSplitView: WorkflowsSplitView,
    useRunRoutineToChat: useRunRoutineToChatPort,
    loadWorkflowShortcutListeners,
    // projects PR4a
    VoiceAssistantTrigger:
      VoiceAssistantTrigger as unknown as WorkHostPorts['routines']['VoiceAssistantTrigger'],
    useRunAnyWorkflow:
      useRunAnyWorkflow as unknown as WorkHostPorts['routines']['useRunAnyWorkflow'],
    // projects PR4b
    RoutineRunChatHeader: RoutineRunChatHeader,
    CreateWorkflowApprovalCard: CreateWorkflowApprovalCard,
    RoutineCreateGuidedChat,
    detectShortcutConflict,
    RecordingWorkflowSummary,
    WorkflowEditorBoldButton: BoldButton,
    WorkflowEditorItalicButton: ItalicButton,
    WorkflowEditorBulletListButton: BulletListButton,
    useOpenRoutinesSurface,
    TeamsSelection,
    EvidenceCircleLoader,
    useOpenEvidencePanel,
    SidebarNavRowStatusIcon,
    resolveChatSidebarNavRowStatus,
  },
  shell: {
    useIsRpaRecorderEnabled,
    useIsWorkflowsEnabled,
    useIsSessionRoutineRunsEnabled,
    useIsAskHeidiSourcesShortcutsEnabled,
    useIsChatObjectRefsEnabled,
    isSlideBuilderEnabled: IS_SLIDE_BUILDER_ENABLED,
    isChatAttachmentsEnabled: IS_CHAT_ATTACHMENTS_ENABLED,
    parseTimeStamp,
    isLogoutInFlight,
    useAreShortcutsDisabled,
    creditAllowanceRefusal: {
      remember: rememberCreditAllowanceRefusal,
      clear: clearCreditAllowanceRefusal,
      nudge: nudgeCreditAllowanceRefusal,
      shouldSkipFollowUpSend: shouldSkipCreditAllowanceFollowUpSend,
      useValue: useCreditAllowanceRefusalValue,
    },
    useStopGeneratingHotkey,
    resolveWorkBillingAnalyticsProps,
    subscribeAuthTokenChange,
    useNavigate,
    useRouteRead,
    useReplaceRouteQuery,
    useQueryKindeProperties,
    // projects artifacts
    IS_INSET_DESIGN_ENABLED,
    useIsAgentHomeV1Enabled,
    useIsPatientListEnabled,
    useTrackWorkArtifactCanvasActions,
    trackWorkArtifactCreated,
    trackWorkArtifactExported,
    cn: cn as WorkHostPorts['shell']['cn'],
    reportBoundaryError:
      reportBoundaryError as WorkHostPorts['shell']['reportBoundaryError'],
    ChunkErrorMessage,
    createLazyChunk:
      createLazyChunk as unknown as WorkHostPorts['shell']['createLazyChunk'],
    // projects components
    useIsCountrySpecificEhrSelectionEnabled,
    personalizationSharedMessages: personalizationSharedMessages,
    useUserCountryCode,
    isProductFeedbackAvailable,
    getRegion: getRegion,
    useQueryUser: useQueryUser,
    trackNurtureEvent,
    trackUpgradeActioned,
    useIsNangoIntegrationsEnabled,
    useWhiteLabelSafe,
    useIsCmdShiftHOverlayEnabled,
    isMacOS,
    OpenTemplateEditorBlock:
      OpenTemplateEditorBlockHost as unknown as WorkHostPorts['shell']['OpenTemplateEditorBlock'],
    TemplatePinBlock:
      TemplatePinBlockHost as unknown as WorkHostPorts['shell']['TemplatePinBlock'],
    WorkArtefactCreatedCard:
      WorkArtefactCreatedCard as unknown as WorkHostPorts['shell']['WorkArtefactCreatedCard'],
    reactQueryClient,
    // projects PR4a
    useRouteInterception:
      useRouter as unknown as WorkHostPorts['shell']['useRouteInterception'],
    cancelNavMenuSwitchTracking,
    ComposerDictateSlot:
      ComposerDictateSlot as unknown as WorkHostPorts['shell']['ComposerDictateSlot'],
    renderPlaceholderSlots:
      renderPlaceholderSlots as WorkHostPorts['shell']['renderPlaceholderSlots'],
    useAnyDialogDrawerOpen,
    useIsEvidenceInlineImagesEnabled,
    useIsEvidenceSubagentResidualCapabilitiesEnabled,
    useIsWorkEvidenceCitationsEnabled,
    useIsWorkSessionArtifactV2Enabled,
    useIsUKOrEU,
    useMediaQuery,
    getPlatform,
    evidenceShortcutRunEventName: TrackingEventName.EvidenceShortcutRun,
    isOnPrem,
    isDevelopmentBuild: process.env.NODE_ENV === 'development',
    // projects PR4b
    ExperienceFeedbackNudge: ExperienceFeedbackNudge,
    mergeCreditAllowanceGhost: mergeCreditAllowanceGhost,
    useIsProjectsContextualLoaderPhrasesEnabled,
    useIsWorkLetterArtifactEnabled,
    useIsEntitlementEnabled: useIsEntitlementEnabled,
    useSetGetMobileAppModalOpen,
    mobileAppHeaderMessages,
    useIsWorkInSessionEnabled,
    useIsFlagProviderReady,
    DEFAULT_DATE_FORMAT,
  },
  bridge: {
    EvidenceTimelineBody:
      EvidenceTimelineBody as unknown as WorkHostPorts['bridge']['EvidenceTimelineBody'],
    useProjectEvidenceSession: useProjectEvidenceSession,
    buildEvidenceSessionFromPayload: buildEvidenceSessionFromPayload,
    readEvidenceRenderPayload: readEvidenceRenderPayload,
    WorkSourcesPopover:
      WorkSourcesPopover as unknown as WorkHostPorts['bridge']['WorkSourcesPopover'],
    stripCiteTags,
    stripImgTags,
    buildCiteMarkers,
    buildImageMarkers,
    buildReferences: buildReferences,
    buildCitationRegistry,
    buildImageRegistry,
    parseEvidenceAgentSearchPayload,
    toChatSource: toChatSource,
    useEvidenceAnswerComponents: useEvidenceAnswerComponents,
    renderCreateTemplateToolUse:
      renderCreateTemplateToolUse as WorkHostPorts['bridge']['renderCreateTemplateToolUse'],
    renderUpdateWordLibraryToolUse:
      renderUpdateWordLibraryToolUse as WorkHostPorts['bridge']['renderUpdateWordLibraryToolUse'],
    get CreateTemplateBlock() {
      return chatToolBlocks().CreateTemplateBlock;
    },
    get UpdateWordLibraryBlock() {
      return chatToolBlocks().UpdateWordLibraryBlock;
    },
    useBlockErrorReporter,
    handleEvidenceChunkStreamEvent: handleEvidenceChunkStreamEvent,
    // projects PR4b
    readEvidenceSession,
    subscribeEvidenceSessions,
  },
  ads: {
    useIsEvidenceAdsEnabled,
    EvidenceLoadingAdPlacement,
  },
});

export interface AppPorts {
  workHome: WorkHomePorts;
  workHost: WorkHostPorts;
}

/** Builds the Work port bundles. Runs on the package's first read, never at module scope. */
export const createAppPorts = (): AppPorts => {
  const workHome = buildWorkHomePorts();
  return { workHome, workHost: buildWorkHostBundles(workHome) };
};

let built: AppPorts | undefined;
const appPorts = (): AppPorts => (built ??= createAppPorts());

configureWorkHomePorts(() => appPorts().workHome);
configureWorkHostPorts(() => appPorts().workHost);
